import { Form, Input, FormProps, InputNumber, FormItemProps, Button } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import FRadioGroup from 'components/FRadioGroup';
import { VoucherType, RewardType, VoucherDisplayPosition } from 'services/api/voucher';
import { regionMonetaryUnitMap, upperCaseRegionRegionMap } from 'services/config/region';
import dayjs, { Dayjs } from 'lib/dayjs';
import { useVoucherDetailShow, useVoucherDetailData } from 'hooks/business/voucherDetail';
import { inputPosInt, inputPosDecimal, limitPrecision } from 'utils/inputCheck';
import FormWrap from './styled/FormWrap';
import AddProduct from '../ProductSelector/AddProduct';
import useVoucherValues from './useVoucherValues';
import CacheTips from './CacheTips';
import { generateVoucherCode, hasValue } from './utils';
import { DatePicker } from 'components/DatePicker';

const { RangePicker } = DatePicker;

export interface KwaiVoucherFormProps extends FormProps {
  storeName?: string;
  storeId: string;
  region: General.UpperCaseRegion;
  status?: Conversation.Voucher.Status;
  dynamicRules?: Record<string, FormItemProps['rules']>;
  disableCache?: boolean;
}

const FormRegionConfig: Record<
  string,
  {
    /** 金额递增步长 */
    step: number;
    /** 折扣金额 最小 */
    minDiscountAmount: number;
    /** 折扣金额 最大 */
    maxDiscountAmount: number;
    /** 折扣百分比 最小 */
    minDiscount: number;
    /** 折扣百分比 最大 */
    maxDiscount: number;
    /** 最低消费金额 最小 */
    minMinBasketPrice: number;
    /** 最低消费金额 最大 */
    maxMinBasketPrice: number;
    /** 最高优惠金额 最小 */
    minMaxPrice: number;
    /** 最高优惠金额 最大 */
    maxMaxPrice: number;
  }
> = {
  BR: {
    step: 0.01,
    minDiscountAmount: 0.1,
    maxDiscountAmount: 1000000000,
    minDiscount: 1,
    maxDiscount: 99,
    minMinBasketPrice: 0.01,
    maxMinBasketPrice: 9999999999,
    minMaxPrice: 0.02,
    maxMaxPrice: 1000000000,
  },
  ID: {
    step: 1,
    minDiscountAmount: 1,
    maxDiscountAmount: 9999999999,
    minDiscount: 1,
    maxDiscount: 99,
    minMinBasketPrice: 1,
    maxMinBasketPrice: 9999999999,
    minMaxPrice: 1,
    maxMaxPrice: 9999999999,
  },
  DEFAULT: {
    step: 0.01,
    minDiscountAmount: 0.1,
    maxDiscountAmount: 1000000000,
    minDiscount: 1,
    maxDiscount: 99,
    minMinBasketPrice: 0.01,
    maxMinBasketPrice: 9999999999,
    minMaxPrice: 0.02,
    maxMaxPrice: 1000000000,
  },
};

const isDisabled = (fieldName: string, status?: Conversation.Voucher.Status) => {
  const formDisabledStatus: { [key: string]: Conversation.Voucher.Status[] } = {
    voucherType: ['SCHEDULED', 'ONGOING'],
    voucherName: ['ONGOING'],
    times: ['ONGOING'],
    displayCollectTime: ['SCHEDULED', 'ONGOING'],
    voucherCode: ['SCHEDULED', 'ONGOING'],
    rewardType: ['ONGOING'],
    percentage: ['ONGOING'],
    discountAmount: ['ONGOING'],
    maxPrice: ['ONGOING'],
    minBasketPrice: ['ONGOING'],
    perCustomerLimit: ['ONGOING'],
    itemIdList: [],
    displayChannel: ['SCHEDULED', 'ONGOING', 'ENDED'],
  };

  if (Array.isArray(formDisabledStatus[fieldName]) && status) {
    return formDisabledStatus[fieldName].includes(status);
  }
  return false;
};

const KwaiVoucherForm = (props: KwaiVoucherFormProps) => {
  const initStartTime = dayjs().add(2, 'minutes');
  const initEndTime = dayjs(initStartTime).add(1, 'day');

  const {
    status,
    form,
    storeName,
    storeId,
    region = 'ID',
    initialValues = {
      voucherType: VoucherType.Shop,
      rewardType: RewardType.FixAmount,
      times: [initStartTime, initEndTime],
      voucherCode: generateVoucherCode(),
      usageQuantity: 1,
      perCustomerLimit: 1,
      itemIdList: [],
    },
    dynamicRules = {},
    disableCache,
    ...rest
  } = props;
  const { t } = useTranslate();
  const [type, setType] = useState(VoucherType.Shop);
  const [discount, setDiscount] = useState(RewardType.FixAmount);

  const { [region]: lowRegion } = upperCaseRegionRegionMap;
  const { [lowRegion]: moneyUnit } = regionMonetaryUnitMap;
  const formRegionConfig = FormRegionConfig[region] || FormRegionConfig.DEFAULT;

  const { clearVoucherFormCache, setVoucherFormCache, getVoucherFormCache } = useVoucherValues();
  const [showCacheTips, setShowCacheTips] = useState(false);
  const [displaySettings, setDisplaySettings] = useState<number | undefined>();

  const voucherDetailShow = useVoucherDetailShow();
  const voucherDetailData = useVoucherDetailData();

  const typeOptions = useMemo(() => {
    return [
      {
        label: t('domain.coupon.type.store'),
        value: VoucherType.Shop,
      },
      {
        label: t('domain.coupon.type.product'),
        value: VoucherType.Product,
      },
    ];
  }, [t]);

  const discountTypeOptions = useMemo(() => {
    return [
      {
        label: t('domain.coupon.discountType.discountPercentage'),
        value: RewardType.DiscountPercentage,
      },
      {
        label: t('domain.coupon.field.discountAmount'),
        value: RewardType.FixAmount,
      },
    ];
  }, [t]);

  const displaySettingsOptions = useMemo(() => {
    return [
      {
        label: t('domain.coupon.displaySettings.displayAllPages'),
        value: VoucherDisplayPosition.AllPage,
      },
      {
        label: t('domain.coupon.displaySettings.distributeViaCode'),
        value: undefined,
      },
    ];
  }, [t]);

  useEffect(() => {
    if (!disableCache) {
      const cached = getVoucherFormCache(storeId);
      if (cached && !status) {
        form?.setFieldsValue(cached);
        setShowCacheTips(true);
      }
    }

    const displayChannel = form?.getFieldValue('displayChannel');
    if (displayChannel) {
      setDisplaySettings(displayChannel);
    }
    const voucherType = form?.getFieldValue('voucherType');
    if (voucherType) {
      setType(voucherType);
    }
    const rewardType = form?.getFieldValue('rewardType');
    if (rewardType) {
      setDiscount(rewardType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableCache, form]);

  useEffect(() => {
    if (voucherDetailShow) {
      voucherDetailData?.voucherType && setType(voucherDetailData.voucherType);
      voucherDetailData?.rewardType && setDiscount(voucherDetailData.rewardType);
    }
  }, [voucherDetailData, voucherDetailShow]);

  const isCreateStatus = useMemo(() => !status, [status]);

  return (
    <Form
      {...rest}
      layout="vertical"
      initialValues={initialValues}
      scrollToFirstError
      form={form}
      onValuesChange={(_, allValues) => {
        if (isCreateStatus) {
          setVoucherFormCache(storeId, allValues);
        }
      }}
    >
      {!disableCache && (
        <CacheTips
          visible={showCacheTips}
          onClick={() => {
            setType(VoucherType.Shop);
            clearVoucherFormCache(storeId);
            form?.resetFields();
            setShowCacheTips(false);
          }}
        />
      )}
      {storeName ? (
        <FormWrap>
          <Form.Item label={t('domain.coupon.field.shopName')} style={{ marginBottom: 0 }}>
            {storeName}
          </Form.Item>
        </FormWrap>
      ) : null}
      <FormWrap title={t('scene.chatWorkspace.couponForm.title.basicInfo')}>
        <Form.Item label={t('domain.coupon.field.type')} name="voucherType" required>
          <FRadioGroup
            options={typeOptions}
            value={type}
            disabled={voucherDetailShow || isDisabled('voucherType', status)}
            onChange={(v) => {
              setType(v as number);
              form?.setFieldsValue({
                voucherType: v,
              });
              if (v === VoucherType.Shop) {
                form?.setFieldsValue({
                  itemIdList: [],
                });
              }
            }}
          />
        </Form.Item>
        <Form.Item
          label={t('domain.coupon.field.name')}
          name="voucherName"
          required
          rules={[
            { required: true, message: t('common.validation.required') },
            ...(dynamicRules.voucherName || []),
          ]}
        >
          <Input
            placeholder={t('scene.chatWorkspace.couponForm.placeholder.couponName')}
            disabled={voucherDetailShow || isDisabled('voucherName', status)}
          />
        </Form.Item>
        <Form.Item
          label={t('domain.coupon.field.validityPeriod')}
          name="times"
          required
          rules={[
            {
              required: true,
              message: t('common.validation.requiredSelect'),
              type: 'array',
            },
            {
              validator: (_, value: [Dayjs, Dayjs]) => {
                if (!isDisabled('times', status) && value) {
                  const [startTime, endTime] = value;
                  if (startTime.isBefore(Date.now())) {
                    return Promise.reject(
                      new Error(
                        t('scene.chatWorkspace.couponForm.validation.startTimeNotEarlierThanNow'),
                      ),
                    );
                  }
                  if (endTime.diff(startTime, 'days') > 180) {
                    return Promise.reject(
                      new Error(
                        t('scene.chatWorkspace.couponForm.validation.endTimeNotExceedMaxMonths', {
                          months: 6,
                        }),
                      ),
                    );
                  }
                  if (endTime.diff(startTime, 'minutes') < 10) {
                    return Promise.reject(
                      new Error(
                        t(
                          'scene.chatWorkspace.couponForm.validation.endTimeAtLeastMinutesAfterStart',
                          { minutes: 10 },
                        ),
                      ),
                    );
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <RangePicker
            showTime
            disabled={voucherDetailShow || isDisabled('times', status)}
            disabledDate={(current) => {
              return current.isSameOrBefore(dayjs(Date.now()));
            }}
          />
        </Form.Item>

        <Form.Item
          label={t('domain.coupon.field.code')}
          name="voucherCode"
          rules={[
            {
              validator: (_, value) => {
                if (value && !/^[A-Z0-9]+$/.test(value)) {
                  return Promise.reject(
                    new Error(t('scene.chatWorkspace.couponForm.placeholder.couponCode')),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            showCount
            maxLength={5}
            placeholder={t('scene.chatWorkspace.couponForm.placeholder.couponCode')}
            disabled={voucherDetailShow || isDisabled('voucherCode', status)}
            addonAfter={
              isDisabled('voucherCode', status) ? null : (
                <Button
                  type="link"
                  onClick={() => {
                    const code = generateVoucherCode();
                    form?.setFieldsValue({
                      voucherCode: code,
                    });
                  }}
                >
                  {t('scene.chatWorkspace.couponForm.action.regenerateCode')}
                </Button>
              )
            }
            onChange={(e) => {
              const { value } = e.target;
              if (/^[A-Z0-9]+$/.test(value)) {
                form?.setFieldsValue({
                  voucherCode: value.toUpperCase(),
                });
              }
            }}
            onBlur={(e) => {
              const { value } = e.target;
              if (value) {
                form?.setFieldsValue({
                  voucherCode: value.toUpperCase(),
                });
              }
            }}
          />
        </Form.Item>
      </FormWrap>
      <FormWrap title={t('scene.chatWorkspace.couponForm.title.discountSettings')}>
        <Form.Item label={t('domain.coupon.field.discountType')} name="rewardType" required>
          <FRadioGroup
            value={discount}
            options={discountTypeOptions}
            disabled={status === 'ONGOING'}
            onChange={(v) => {
              setDiscount(v as number);
              form?.setFieldsValue({
                rewardType: v,
              });
              setTimeout(
                () =>
                  form?.validateFields([
                    'percentage',
                    'discountAmount',
                    'maxPrice',
                    'minBasketPrice',
                  ]),
                0,
              );
            }}
          />
        </Form.Item>

        {discount === RewardType.DiscountPercentage ? (
          <Form.Item
            label={t('domain.coupon.discountType.discountPercentage')}
            name="percentage"
            required
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(new Error(t('common.validation.required')));
                  }
                  if (formRegionConfig.step < 1) {
                    const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                    if (!v.precisionMatch) {
                      form?.setFieldsValue({ percentage: v.precisionVal });
                    }
                  }
                  if (!isDisabled('percentage', status) && hasValue(value)) {
                    if (value < formRegionConfig.minDiscount) {
                      return Promise.reject(
                        new Error(
                          t('scene.chatWorkspace.couponForm.validation.minDiscountPercentage', {
                            discountPercentage: formRegionConfig.minDiscount,
                          }),
                        ),
                      );
                    }
                    if (value > formRegionConfig.maxDiscount) {
                      return Promise.reject(
                        new Error(
                          t('scene.chatWorkspace.couponForm.validation.maxDiscountPercentage', {
                            discountPercentage: formRegionConfig.maxDiscount,
                          }),
                        ),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
              ...(dynamicRules.percentage || []),
            ]}
          >
            <InputNumber
              addonAfter="%"
              disabled={voucherDetailShow || isDisabled('percentage', status)}
              placeholder={t('common.placeholder.input')}
              min={formRegionConfig.minDiscount}
              max={formRegionConfig.maxDiscount}
              precision={0}
              step={1}
              onKeyPress={inputPosInt}
            />
          </Form.Item>
        ) : null}
        {discount === RewardType.FixAmount ? (
          <Form.Item
            label={t('domain.coupon.field.discountAmount')}
            name="discountAmount"
            dependencies={['minBasketPrice']}
            required
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(new Error(t('common.validation.required')));
                  }
                  if (formRegionConfig.step < 1) {
                    const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                    if (!v.precisionMatch) {
                      form?.setFieldsValue({ discountAmount: v.precisionVal });
                    }
                  }
                  if (!isDisabled('discountAmount', status)) {
                    const minBasketPrice = form?.getFieldValue('minBasketPrice');
                    if (hasValue(value) && hasValue(minBasketPrice) && value > minBasketPrice) {
                      return Promise.reject(
                        new Error(
                          t('scene.chatWorkspace.couponForm.validation.discountLessThanMinSpend'),
                        ),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
              ...(dynamicRules.discountAmount || []),
            ]}
          >
            <InputNumber
              addonAfter={moneyUnit}
              disabled={voucherDetailShow || isDisabled('discountAmount', status)}
              placeholder={t('common.placeholder.input')}
              min={formRegionConfig.minDiscountAmount}
              max={formRegionConfig.maxDiscountAmount}
              precision={formRegionConfig.step ? Math.abs(Math.log10(formRegionConfig.step)) : 0}
              step={formRegionConfig.step}
              onKeyPress={formRegionConfig.step === 1 ? inputPosInt : inputPosDecimal}
            />
          </Form.Item>
        ) : null}
        {discount === RewardType.DiscountPercentage ? (
          <Form.Item
            label={t('domain.coupon.field.maxDiscount')}
            name="maxPrice"
            required
            dependencies={['percentage', 'minBasketPrice']}
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(new Error(t('common.validation.required')));
                  }
                  if (formRegionConfig.step < 1) {
                    const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                    if (!v.precisionMatch) {
                      form?.setFieldsValue({ maxPrice: v.precisionVal });
                    }
                  }
                  if (!isDisabled('maxPrice', status) && hasValue(value)) {
                    const percentage = form?.getFieldValue('percentage');
                    const minBasketPrice = form?.getFieldValue('minBasketPrice');
                    if (
                      percentage &&
                      minBasketPrice &&
                      value < (minBasketPrice * percentage) / 100
                    ) {
                      return Promise.reject(
                        new Error(
                          t('scene.chatWorkspace.couponForm.validation.maxCapNotLessThanMinSpend', {
                            discountPercentage: percentage,
                          }),
                        ),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
              ...(dynamicRules.maxPrice || []),
            ]}
          >
            <InputNumber
              placeholder={t('common.placeholder.input')}
              addonAfter={moneyUnit}
              disabled={voucherDetailShow || isDisabled('maxPrice', status)}
              min={formRegionConfig.minMaxPrice}
              max={formRegionConfig.maxMaxPrice}
              precision={formRegionConfig.step ? Math.abs(Math.log10(formRegionConfig.step)) : 0}
              step={formRegionConfig.step}
              onKeyPress={formRegionConfig.step === 1 ? inputPosInt : inputPosDecimal}
            />
          </Form.Item>
        ) : null}

        <Form.Item
          label={t('domain.coupon.field.minSpend')}
          name="minBasketPrice"
          dependencies={['discountAmount']}
          required
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject(new Error(t('common.validation.required')));
                }
                if (formRegionConfig.step < 1) {
                  const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                  if (!v.precisionMatch) {
                    form?.setFieldsValue({ minBasketPrice: v.precisionVal });
                  }
                }
                if (!isDisabled('minBasketPrice', status)) {
                  if (discount === RewardType.FixAmount) {
                    const discountAmount = form?.getFieldValue('discountAmount');
                    if (hasValue(value) && hasValue(discountAmount) && discountAmount > value) {
                      return Promise.reject(
                        new Error(
                          t('scene.chatWorkspace.couponForm.validation.discountLessThanMinSpend'),
                        ),
                      );
                    }
                  }
                }
                return Promise.resolve();
              },
            },
            ...(dynamicRules.minBasketPrice || []),
          ]}
        >
          <InputNumber
            placeholder={t('common.placeholder.input')}
            addonAfter={moneyUnit}
            disabled={voucherDetailShow || isDisabled('minBasketPrice', status)}
            min={formRegionConfig.minMinBasketPrice}
            max={formRegionConfig.maxMinBasketPrice}
            precision={formRegionConfig.step ? Math.abs(Math.log10(formRegionConfig.step)) : 0}
            step={formRegionConfig.step}
            onKeyPress={formRegionConfig.step === 1 ? inputPosInt : inputPosDecimal}
          />
        </Form.Item>

        <Form.Item
          label={t('domain.coupon.field.totalQuantity')}
          name="usageQuantity"
          required
          rules={[
            {
              required: true,
              message: t('common.validation.required'),
            },
            ...(dynamicRules.usageQuantity || []),
          ]}
        >
          <InputNumber
            disabled={voucherDetailShow}
            placeholder={t('common.placeholder.input')}
            min={1}
            max={99999999}
            precision={0}
            step={1}
            onKeyPress={inputPosInt}
          />
        </Form.Item>
        <Form.Item
          label={t('domain.coupon.field.usageLimitPerUser')}
          name="perCustomerLimit"
          required
          dependencies={['usageQuantity']}
          rules={[
            { required: true, message: t('common.validation.required') },
            {
              validator: (_, value) => {
                const usageQuantity = form?.getFieldValue('usageQuantity');
                if (
                  !isDisabled('perCustomerLimit', status) &&
                  hasValue(value) &&
                  hasValue(usageQuantity) &&
                  value > usageQuantity
                ) {
                  return Promise.reject(
                    new Error(
                      t(
                        'scene.chatWorkspace.couponForm.validation.receivedCountNotGreaterThanTotal',
                      ),
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
            ...(dynamicRules.perCustomerLimit || []),
          ]}
        >
          <InputNumber
            min={1}
            max={50}
            precision={0}
            step={1}
            disabled={voucherDetailShow || isDisabled('perCustomerLimit', status)}
            placeholder={t('common.placeholder.input')}
            onKeyPress={inputPosInt}
          />
        </Form.Item>
      </FormWrap>
      <FormWrap title={t('scene.chatWorkspace.couponForm.title.otherSettings')}>
        <Form.Item label={t('domain.coupon.field.displaySettings')} name="displayChannel" required>
          <FRadioGroup
            value={displaySettings}
            disabled={isDisabled('displayChannel', status)}
            options={displaySettingsOptions}
            onChange={(v) => {
              setDisplaySettings(v as number);
              if (v === -1) {
                form?.setFieldsValue({
                  displayChannel: undefined,
                });
              } else {
                form?.setFieldsValue({
                  displayChannel: v,
                });
              }
            }}
          />
        </Form.Item>
        {type === VoucherType.Product && storeId ? (
          <Form.Item
            label={t('domain.coupon.field.eligibleProducts')}
            name="itemIdList"
            required
            rules={[
              {
                required: true,
                message: t('common.validation.requiredSelect'),
                type: 'array',
              },
              ...(dynamicRules.itemIdList || []),
            ]}
          >
            <AddProduct
              storeId={storeId}
              disabled={voucherDetailShow || isDisabled('itemIdList', status)}
            />
          </Form.Item>
        ) : null}
      </FormWrap>
    </Form>
  );
};

export default KwaiVoucherForm;
